import { useEffect, useMemo, useState } from 'react'

import merchantApi from '../../services/merchantApi'

import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar
} from 'recharts'

import './Dashboard.scss'
import Header from '../../components/Header/Header'
import AppTable from '../../components/AppTable/AppTable'
import { useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react/dist/iconify.js'
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Grid,
} from '@mui/material'

const DEFAULT_STATS = [
  {
    label: 'PLATFORM GMV',
    value: 'RM0.00',
    trend: null,
    positive: true,
  },
  {
    label: 'ORDERS',
    value: '0',
    trend: null,
    positive: true,
  },
  {
    label: 'AVG ORDER VALUE',
    value: 'RM0.00',
    trend: null,
    positive: true,
  },
  {
    label: 'ACTIVE MERCHANTS',
    value: '0',
  },
]

const DEFAULT_REVENUE_DATA = [
  { date: 'No data', revenue: 0 },
]

const getPayload = (response) => response?.data || response || {}

const asNumber = (value, fallback = 0) => {
  if (value === null || value === undefined || value === '') return fallback
  const cleaned = typeof value === 'string' ? value.replace(/[^0-9.-]/g, '') : value
  const number = Number(cleaned)
  return Number.isFinite(number) ? number : fallback
}

const formatMoney = (value) => {
  const amount = asNumber(value)

  return `RM${amount.toLocaleString('en-MY', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

const formatNumber = (value) => asNumber(value).toLocaleString('en-MY')

const formatActivityTime = (value) => {
  if (!value) return ''

  const date = new Date(String(value).replace(' ', 'T'))
  if (Number.isNaN(date.getTime())) return value

  return date.toLocaleString('en-MY', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

const formatTrend = (value) => {
  if (value === null || value === undefined || value === '') return null
  const number = asNumber(value)
  const sign = number > 0 ? '+' : ''
  return `${sign}${number.toFixed(1)}%`
}

const pick = (source, keys, fallback = undefined) => {
  for (const key of keys) {
    if (source && source[key] !== undefined && source[key] !== null) {
      return source[key]
    }
  }

  return fallback
}

const normalizeDashboard = (response) => {
  const payload = getPayload(response)
  const kpis = payload.kpis || {}

  const stats = [
    {
      label: 'PLATFORM GMV',
      value: formatMoney(kpis.platform_gmv),
      trend: formatTrend(kpis.gmv_vs_previous),
      positive: asNumber(kpis.gmv_vs_previous) >= 0,
    },
    {
      label: 'ORDERS',
      value: formatNumber(kpis.total_orders),
      trend: formatTrend(kpis.orders_vs_previous),
      positive: asNumber(kpis.orders_vs_previous) >= 0,
    },
    {
      label: 'AVG ORDER VALUE',
      value: formatMoney(kpis.avg_order_value),
      trend: formatTrend(kpis.aov_vs_previous),
      positive: asNumber(kpis.aov_vs_previous) >= 0,
    },
    {
      label: 'ACTIVE MERCHANTS',
      value: formatNumber(kpis.active_merchants),
    },
  ]

  const trendRows = Array.isArray(payload.gmv_trend) ? payload.gmv_trend : []
  const revenueData = trendRows.length
    ? trendRows.map((row) => ({
      date: row.day || row.date || '',
      revenue: asNumber(row.gmv),
      orders: asNumber(row.orders),
    }))
    : DEFAULT_REVENUE_DATA

  const merchantRows = Array.isArray(payload.top_merchants) ? payload.top_merchants : []
  const topMerchants = merchantRows.map((merchant, index) => ({
    id: pick(merchant, ['id', 'merchant_id'], index + 1),
    rank: index + 1,
    name: pick(merchant, ['merchant_name', 'name', 'business_name'], 'Unnamed merchant'),
    orders: formatNumber(pick(merchant, ['orders', 'total_orders', 'order_count'], 0)),
    gmv: formatMoney(pick(merchant, ['gmv', 'platform_gmv', 'total_gmv'], 0)),
    trend: asNumber(pick(merchant, ['gmv_vs_previous', 'vs_yesterday', 'trend'], 0)),
  }))

  const logRows = Array.isArray(payload.activity_logs) ? payload.activity_logs : []
  const activityLogs = logRows.map((log, index) => ({
    id: pick(log, ['id'], `${log.time || 'activity'}-${index}`),
    merchantName: log.merchant_name || 'Cuscapi',
    title: log.section
      ? `${log.section.charAt(0).toUpperCase()}${log.section.slice(1)}`
      : 'Activity',
    message: log.error || log.message || log.description || 'Activity completed.',
    time: log.time || log.created_at || '',
    status: log.error ? 'error' : (log.status || 'success'),
  }))

  return {
    stats,
    revenueData,
    topMerchants,
    activityLogs,
    gmvTotal: formatMoney(kpis.platform_gmv),
  }
}

const getDashboardParams = (dateFilter) => ({ range: dateFilter })

const DATE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This week' },
  { value: 'this_month', label: 'This month' },
]

export default function Dashboard() {
  const [dateFilter, setDateFilter] = useState('today')
  const [dashboard, setDashboard] = useState({
    stats: DEFAULT_STATS,
    revenueData: DEFAULT_REVENUE_DATA,
    topMerchants: [],
    activityLogs: [],
    gmvTotal: 'RM0.00',
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [userInfo, setUserInfo] = useState(null)
  const navigate = useNavigate()

  const todayLabel = useMemo(() => new Date().toLocaleDateString('en-MY', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }), [])
  const greeting = useMemo(() => {
    const hour = new Date().getHours()

    if (hour < 12) return 'Good morning'
    if (hour < 18) return 'Good afternoon'
    return 'Good evening'
  }, [])

  useEffect(() => {
    loadUserInfo()
  }, [])

  useEffect(() => {
    let active = true

    const loadDashboard = async () => {
      setLoading(true)
      setError('')

      try {
        const response = await merchantApi.dashboard.get(getDashboardParams(dateFilter))
        if (active) {
          setDashboard(normalizeDashboard(response))
        }
      } catch (err) {
        if (active) {
          setError(err.message || 'Unable to load dashboard.')
          setDashboard((prev) => ({ ...prev, stats: DEFAULT_STATS, revenueData: DEFAULT_REVENUE_DATA }))
        }
      } finally {
        if (active) setLoading(false)
      }
    }

    loadDashboard()

    return () => {
      active = false
    }
  }, [dateFilter])

  const loadUserInfo = async () => {
    try {
      const userInfoResponse = await merchantApi.auth.me()
      setUserInfo(userInfoResponse?.data || null)
    } catch (err) {
      console.error('Failed to load user info:', err)
    }
  }

  const { stats, revenueData, topMerchants, activityLogs, gmvTotal } = dashboard
  const topMerchantColumns = [
    {
      field: 'rank',
      header: '#',
      render: (merchant) => `#${merchant.rank}`,
    },
    {
      field: 'name',
      header: 'Merchant',
      render: (merchant) => (
        <div className="merchant-table__merchant">
          {merchant.name}
        </div>
      ),
    },
    { field: 'orders', header: 'Orders' },
    {
      field: 'gmv',
      header: 'GMV',
      render: (merchant) => (
        <div className="merchant-table__gmv">
          {merchant.gmv}
        </div>
      ),
    },
    {
      field: 'trend',
      header: 'Vs Yesterday',
      render: (merchant) => (
        <Box
          component="span"
          className={`merchant-table__trend ${merchant.trend >= 0
            ? 'merchant-table__trend--positive'
            : 'merchant-table__trend--negative'
            }`}
        >
          <Icon
            icon={merchant.trend >= 0 ? 'ph:arrow-up' : 'ph:arrow-down'}
            height={12}
          />
          {Math.abs(merchant.trend)}%
        </Box>
      ),
    },
  ]

  return (
    <Box className="dashboard-page">
      {/* =======================================================
                HEADER
            ======================================================= */}
      <Header
        items={[
          { label: 'Cuscapi', to: '/' },
          { label: 'Dashboard', current: true },
        ]}
      />
      <Box className="dashboard-topbar">
        <Box className="dashboard-heading">
          <Box>
            <div className="dashboard-title">
              {greeting}, {userInfo?.name || 'User'}
            </div>

            <div className="dashboard-subtitle">
              {todayLabel}
            </div>
          </Box>
        </Box>

        <Box className="dashboard-filters">
          <Box className="report-actions">
            <Box className="report-tabs">
              {
                DATE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    className={dateFilter === option.value ? 'active' : ''}
                    onClick={() => setDateFilter(option.value)}
                    type="button"
                  >
                    {option.label}
                  </button>
                ))
              }
            </Box>
            <button
              className="btn-export"
              onClick={() => { navigate('/reports-analytics') }}
              type="button"
              endIcon={<Icon icon="ph:arrow-right" height={16} />}
            >
              View full report <Icon icon="ph:arrow-right" height={16} />
            </button>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" className="dashboard-alert">
          {error}
        </Alert>
      )}

      {loading && (
        <Box className="dashboard-loading" role="status">
          <CircularProgress size={18} />
          Loading dashboard...
        </Box>
      )}

      {/* =======================================================
                STATS
            ======================================================= */}

      <Box className="dashboard-stats">
        {stats.map((item, index) => (
          <Card
            className="dashboard-stat"
            key={item.label || index}
            variant="outlined"
          >
            <div className="dashboard-stat-label">
              {item.label}
            </div>

            <div className="dashboard-stat-value">
              {item.value}
            </div>

            {item.trend && (
              <div
                className={`dashboard-stat-trend ${item.positive
                  ? 'dashboard-stat-trend--positive'
                  : 'dashboard-stat-trend--negative'
                  }`}
              >
                {item.trend}
              </div>
            )}

            {item.meta && (
              <div className="dashboard-stat-meta">
                {item.meta}
              </div>
            )}
          </Card>
        ))}
      </Box>

      {/* =======================================================
                MAIN GRID
            ======================================================= */}

      <Box className="dashboard-grid">
        {/* ===========================
                    CHART
                =========================== */}

        <Card className="dashboard-card" variant="outlined">
          <Box className="dashboard-card-header">
            <div className="dashboard-card-title">
              GMV trend - last 7 days
            </div>
            <div className="dashboard-card-title">
              {gmvTotal}
            </div>
          </Box>

          <Box className="dashboard-chart">
            <ResponsiveContainer
              width="100%"
              height={320}
            >
              <BarChart
                data={revenueData}
                margin={{ top: 10, right: 20, left: -10, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="4 4"
                  vertical={false}
                  stroke="#E5E7EB"
                />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tick={{
                    fill: '#6B7280',
                    fontSize: 12,
                  }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tick={{
                    fill: '#6B7280',
                    fontSize: 12,
                  }}
                  tickFormatter={(value) => `${value / 1000}K`}
                />

                <Tooltip
                  cursor={{
                    fill: '#F8FAFC',
                  }}
                  formatter={(value) => [
                    `RM ${Number(value).toLocaleString()}`,
                    'GMV',
                  ]}
                />

                <Bar
                  dataKey="revenue"
                  radius={[8, 8, 0, 0]}
                  fill="#41C4D9"
                  maxBarSize={52}
                />

              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Card>

        {/* ===========================
                    TOP SELLING
                =========================== */}

        <Card className="dashboard-card" variant="outlined">
          <Box className="dashboard-card-header">
            <div className="dashboard-card-title">
              Activity logs
            </div>
          </Box>

          {!activityLogs.length && (
            <div className="dashboard-empty">
              No activity logs yet.
            </div>
          )}
          {!!activityLogs.length && (
            <div className="activity-logs">
              {activityLogs.map((activity, index) => (
                <div key={activity.id}>
                  <div className="activity-item">
                    <div className={`activity-item__icon activity-item__icon--${activity.status}`}>
                      <Icon icon="ph:circle-fill" height={8} />
                    </div>
                    <div className="activity-item__content">
                      <div className="activity-item__title">{activity.title}</div>
                      <div className="activity-item__message">{activity.message}</div>
                    </div>
                  </div>
                  <div className="activity-item__meta">
                    {activity.merchantName}
                    <Icon icon="ph:circle-fill" height={5} />
                    {formatActivityTime(activity.time)}
                  </div>
                  {index < activityLogs.length - 1 && <Divider style={{ marginTop: 10 }} />}
                </div>
              ))}
            </div>
          )}
        </Card>
      </Box>

      {/* =======================================================
                RECENT ORDERS
            ======================================================= */}

      <Card className="dashboard-card" variant="outlined">

        <Box className="dashboard-card-header">
          <div className="dashboard-card-title">
            Top merchants by GMV today
          </div>
        </Box>

        <AppTable
          columns={topMerchantColumns}
          rows={topMerchants}
          rowKey="id"
          loading={loading}
          emptyText="No merchant GMV data yet."
          noPagination
        />
      </Card>
    </Box>
  )
}
